import React from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useProductChoiceGroup } from './useProductChoiceGroup';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useProductToUIMapping } from '@apps/card/components/ProductUIConfig/useProductToUIMapping';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { Product } from '@apps/card/components/ProductUIConfig/product.types';
import { useCustomizationContext } from '../../useCardCustomization';
import { getDimensions } from '@apps/card/utils';
import { formatProductSizeParts, useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import type { ProductPickerRow } from '@apps/card/components/ProductPicker/ProductPicker.types';
import { ProductPicker, ProductPickerPrice } from '@apps/card/components/ProductPicker/ProductPicker';
import { getProductIconKind } from '@apps/card/components/ProductPicker/ProductIcon';

const isProduct = (input: unknown): input is StationeryProduct & Product => !!input;

type ProductChoiceGroupProps = Readonly<{
  draftId: string;
  /** Dims the group while the caller has work of its own in flight, as a switch does. */
  busy?: boolean;
  /**
   * Stands between a pick and the switch, for a caller that must ask first. Call `select` to go
   * ahead; never calling it drops the pick. Without it, a pick switches straight away.
   */
  confirmSelect?: (productId: string, select: () => void) => void;
}>;

type ProductChoiceGroupContentProps = Readonly<{
  isSwitching: boolean;
  /** The draft has an order in the cart, so the backend will refuse any switch. */
  isLocked: boolean;
  selectProduct: (productId: string) => void;
  choiceProducts: StationeryProduct[];
}>;

const ProductChoiceGroupContent = ({ isSwitching, isLocked, selectProduct, choiceProducts }: ProductChoiceGroupContentProps) => {
  const { tq } = useTranslation('stationery');
  const { activeProduct, priceCalculator } = useStationeryProductPricingContext();
  const { getProductWithUIMapping } = useProductToUIMapping();
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  // Every switch target lives on the draft's own template, so the row titles
  // take the draft's category and size: the same "Postcard 4×6" / "Magnet 4×6"
  // / "Classic Paper 5×7" the PDP picker shows, rather than the bare product
  // name (which would call a postcard "Classic Paper").
  const [templateCategory] = useCustomizationContext(d => d.stationeryTemplateCategory, true);
  const [card] = useCustomizationContext(d => d.config.card, true);
  const { width: widthInches, height: heightInches } = getDimensions(card);
  const sizeLabels = useCardSizeLabels();

  const products = choiceProducts.map(p => getProductWithUIMapping(p)).filter(isProduct);
  const sizedOn = { category: templateCategory, widthInches, heightInches };

  // Undiscounted: the editor has no sale context, and a promo entered at
  // checkout applies there. The PDP is where the sale price is advertised.
  const rows: ProductPickerRow[] = products.map(product => {
    const priceData = priceCalculator.getProductBasePrice(product.id, product.defaultQuantity, undefined, true);
    const unitPriceString = minorUnitsToPriceString(priceData.unitPriceInMinorUnits, { canShowFreeLabel: true });
    const title = formatProductSizeParts(templateCategory, widthInches, heightInches, sizeLabels, product);
    return {
      key: product.id,
      productType: product.productType,
      icon: getProductIconKind(product, sizedOn),
      name: title.name,
      size: title.size,
      price: (
        <ProductPickerPrice
          price={unitPriceString}
          isFree={priceData.unitPriceInMinorUnits === 0}
        />
      ),
      // `isSwitching` dims the group and blocks the pointer, but a button stays in the tab order
      // and Enter still fires it -- and a keyboard select landing mid-undo would start a second
      // product mutation racing the one in flight, since the two paths hold separate locks.
      // `isLocked` keeps the rows visible so the user learns the format CAN change, just not
      // from here.
      disabled: isSwitching || isLocked,
    };
  });

  return (
    <Box>
      <Box
        opacity={isSwitching || isLocked ? 0.6 : 1}
        pointerEvents={isSwitching ? 'none' : 'auto'}
        transition="opacity 0.15s ease"
      >
        <ProductPicker
          rows={rows}
          value={activeProduct.id}
          onSelect={selectProduct}
        />
      </Box>
      {isLocked && (
        <TextV2
          typographyVariant="label2"
          color="mono12"
          marginTop={3}
        >
          {tq`Remove this item from your cart to change its format.`}
        </TextV2>
      )}
    </Box>
  );
};

/**
 * The customizer's product switcher: the same `ProductPicker` the PDP shows,
 * fed from the draft's pricing context and wired to the draft-product mutation.
 */
export const ProductChoiceGroup = ({ draftId, busy, confirmSelect }: ProductChoiceGroupProps) => {
  const { selectProduct, isSwitching, canSelectProduct, isLockedToCartItem, choiceProducts } = useProductChoiceGroup({ draftId });
  if (!canSelectProduct) return null;
  return (
    <ProductChoiceGroupContent
      isSwitching={isSwitching || !!busy}
      isLocked={isLockedToCartItem}
      selectProduct={productId => (confirmSelect ? confirmSelect(productId, () => selectProduct(productId)) : selectProduct(productId))}
      choiceProducts={choiceProducts}
    />
  );
};
